// ---------------------------------------------------------------------------
// query-persister — TanStack Query cache → IndexedDB persistence layer
// ---------------------------------------------------------------------------
// Persists the query cache to IndexedDB so the app renders the last known
// query data on cold start while the network is unreachable. Combined with
// the service worker's stale-while-revalidate route cache, this provides
// fully offline-capable query hydration without polling or setTimeout.
// ---------------------------------------------------------------------------

import {
  saveQueryCacheEntry,
  getAllQueryCacheEntries,
  clearQueryCache,
  type QueryCacheEntry,
} from './indexed-db';

export interface TanStackQueryState {
  queryHash: string;
  queryKey: unknown[];
  data: unknown;
  dataUpdatedAt: number;
  status: 'success' | 'error' | 'pending';
  error?: unknown;
}

const PERSIST_DEBOUNCE_MS = 250;

export interface QueryPersisterOptions {
  /** Maximum number of cached query entries to persist (oldest evicted first) */
  maxEntries?: number;
}

/**
 * Persist a snapshot of the TanStack Query cache to IndexedDB.
 * Called from QueryProvider when the cache mutates.
 */
export async function persistQueryCache(
  state: TanStackQueryState[],
  options: QueryPersisterOptions = {},
): Promise<void> {
  const { maxEntries = 200 } = options;
  const entries = state
    .filter((q) => q.status === 'success')
    .slice(0, maxEntries)
    .map((q): QueryCacheEntry => ({
      queryHash: q.queryHash,
      data: { queryKey: q.queryKey, data: q.data, dataUpdatedAt: q.dataUpdatedAt, status: q.status },
      fetchedAt: Date.now(),
    }));
  await Promise.all(entries.map((e) => saveQueryCacheEntry(e)));
}

/**
 * Hydrate the TanStack Query cache from IndexedDB.
 * Called once at startup before queries mount.
 */
export async function hydrateQueryCache(): Promise<TanStackQueryState[]> {
  const entries = await getAllQueryCacheEntries();
  return entries.map((e): TanStackQueryState => {
    const data = e.data as { queryKey: unknown[]; data: unknown; dataUpdatedAt: number; status: 'success' | 'error' | 'pending' };
    return {
      queryHash: e.queryHash,
      queryKey: data.queryKey,
      data: data.data,
      dataUpdatedAt: data.dataUpdatedAt,
      status: data.status,
    };
  });
}

/**
 * Clear all persisted query entries. Used after logout or after a schema-breaking
 * cache version bump.
 */
export async function resetPersistedQueryCache(): Promise<void> {
  await clearQueryCache();
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced wrapper around persistQueryCache — coalesces rapid cache mutations
 * into a single IDB write per debounce window. Event-driven (no setInterval).
 */
export function debouncedPersist(
  getState: () => TanStackQueryState[],
  options?: QueryPersisterOptions,
): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    persistQueryCache(getState(), options).catch(() => {
      // Best-effort persistence — failure should not break the app
    });
  }, PERSIST_DEBOUNCE_MS);
}

export function cancelPendingPersist(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}